import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useKeenSlider } from 'keen-slider/react';
import 'keen-slider/keen-slider.min.css';
import axios from 'axios';

// Custom animation plugin remains as before
const animation = (slider) => {
  let timeout;
  let mouseOver = false;

  function clearNextTimeout() {
    clearTimeout(timeout);
  }
  function nextTimeout() {
    clearTimeout(timeout);
    if (mouseOver) return;
    timeout = setTimeout(() => {
      slider.next();
    }, 5000);
  }
  slider.on("created", () => {
    slider.container.addEventListener("mouseover", () => {
      mouseOver = true;
      clearNextTimeout();
    });
    slider.container.addEventListener("mouseout", () => {
      mouseOver = false;
      nextTimeout();
    });
    nextTimeout();
    // Ensure the container uses the slower duration by default
    slider.container.style.transitionDuration = '2500ms';
  });
  slider.on("dragStarted", clearNextTimeout);
  slider.on("animationEnded", nextTimeout);
  slider.on("updated", nextTimeout);
};

const BestSellersSlider = () => {
  const [products, setProducts] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [sliderLoaded, setSliderLoaded] = useState(false);

  // Initialize Keen Slider with custom animation
  const [sliderRef, instanceRef] = useKeenSlider(
    {
      loop: false,
      mode: "snap",
      duration: 2500, // Set default duration for drag gestures to 2500ms
      slideChanged(s) {
        setCurrentSlide(s.track.details.rel);
      },
      created(s) {
        setSliderLoaded(true);
        setCurrentSlide(s.track.details.rel);
        s.container.style.transitionDuration = '2500ms'; // enforce duration on creation
      },
      breakpoints: {
        "(min-width: 768px)": {
          slides: { perView: 2.5, spacing: 24 },
        },
        "(min-width: 1024px)": {
          slides: { perView: 4, spacing: 30 },
        },
      },
      slides: {
        perView: 1.2,
        spacing: 16,
        origin: 'auto',
      },
      animation: {
        duration: 2500,
        easing: (t) => 1 - Math.pow(1 - t, 4), // easeOutQuart
      },
    },
  );

  // Force update Keen Slider when products load
  useEffect(() => {
    if (instanceRef.current) instanceRef.current.update();
  }, [products, instanceRef]);

  useEffect(() => {
    axios.get('/api/products/best-sellers')
      .then(res => setProducts(res.data))
      .catch(err => console.error('Failed to load best sellers:', err));
  }, []);

  if (!products || products.length === 0) return null;

  // Use a rounded slide index for navigation
  const roundedSlideIndex = Math.round(currentSlide);
  const maxSlideIndex = instanceRef.current
    ? instanceRef.current.track.details.maxIdx || 0
    : 0;

  return (
    <section className="py-10 bg-white relative">
      <div className="text-center mb-12">
        <h2
          className="text-center uppercase mt-4 mb-0"
          style={{
            color: '#3b3d6e',
            fontFamily: 'Montserrat, sans-serif',
            fontSize: '20px',
            fontWeight: 500,
            fontStyle: 'normal',
            lineHeight: '33px',
            letterSpacing: '4px',
            transition: 'color 0.2s ease-in-out',
            WebkitFontSmoothing: 'antialiased',
          }}
        >
          BEST SELLERS
        </h2>

      </div>

      <div ref={sliderRef} className="keen-slider px-4 max-w-7xl mx-auto">
        {products.map((product) => (
          <div className="keen-slider__slide group p-2 min-w-[250px]" key={product._id}>
            <Link to={`/product/${product.productId}`}>
              <img
                src={`/uploads/${product.photos?.[0]?.replace(/\\/g, '/')}`}
                alt={product.name}
                className="w-full h-[350px] object-cover rounded-lg group-hover:scale-105 transition-transform duration-500"
              />
              <div className="text-center mt-4">
                <h3 className="font-serif text-base">{product.name}</h3>
                <p className="text-sm mt-1">
                  {typeof product.price === 'number' ? `$${product.price.toFixed(2)}` : 'Price unavailable'}
                </p>
              </div>
            </Link>
          </div>
        ))}
      </div>

      {sliderLoaded && instanceRef.current && (
        <>
          {/* LEFT ARROW */}
          {sliderLoaded && currentSlide > 0 && (
            <button
              onClick={() => {
                const prevIndex = Math.max(currentSlide - 4, 0);
                instanceRef.current?.moveToIdx(prevIndex, {
                  duration: 2500,
                  easing: (t) => 1 - Math.pow(1 - t, 4),
                });
              }}
              className="hidden lg:block absolute top-1/2 left-4 -translate-y-1/2 bg-white border border-white rounded-full shadow-md ml-6 p-3 group hover:bg-transparent transition-all duration-300"
            >
              <svg className="h-4 w-4 text-black group-hover:-translate-x-2 transition-transform duration-300"
                viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path d="M15 19l-7-7 7-7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          )}


          {/* RIGHT ARROW */}
          {sliderLoaded && currentSlide < maxSlideIndex && (
            <button
              onClick={() => {
                const nextIndex = Math.min(currentSlide + 4, maxSlideIndex);
                instanceRef.current?.moveToIdx(nextIndex, {
                  duration: 2500,
                  easing: (t) => 1 - Math.pow(1 - t, 4),
                });
              }}
              className="hidden lg:block absolute top-1/2 right-4 -translate-y-1/2 bg-white border border-white rounded-full shadow-md mr-6 p-3 group hover:bg-transparent transition-all duration-300"
            >
              <svg className="h-4 w-4 text-black group-hover:translate-x-2 transition-transform duration-300"
                viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path d="M9 5l7 7-7 7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          )}


        </>
      )}
    </section>
  );
};

export default BestSellersSlider;
